/**
 * Custom Tiptap nodes for the meeting-minutes document schema.
 *
 * Phase 6 architecture (free-flow editor):
 *   - Every text region is a real editable contenteditable. No
 *     more `atom: true` containers with HTML <input> elements.
 *   - Cursor can roam across the entire document; selections can
 *     span any combination of fields; marks (bold/italic/underline)
 *     work everywhere.
 *   - Structured fields (date, assignee, meeting format, etc.)
 *     stay typed as attrs on their containing block, with the
 *     NodeView rendering them as clickable pill buttons that
 *     open small popovers for editing.
 *
 * Hierarchy:
 *
 *   doc
 *    ├── meetingHeader (block)
 *    │    ├── headerTitle  — editable h1 (text*)
 *    │    └── headerMeta   — pills: variant, date, project, location, format
 *    ├── attendeesBlock (block)
 *    │    └── attendeeGroup* (block)
 *    │         ├── firmText      — editable firm name (text*)
 *    │         └── membersText   — editable comma-separated list (text*)
 *    ├── actionItemsBlock (block)
 *    │    └── actionItem* (block)
 *    │         └── text*  — editable description, with optional
 *    │                       inline assigneePill / datePill at end
 *    └── topic+
 *         ├── topicTitle (block, text*)
 *         └── bullet+ (block, text*) with attrs speaker /
 *                       isDirectQuote / ambiguityFlag
 *
 * The doc serializer in serialize.ts walks this tree to produce
 * StructuredContent. Schema ↔ doc round-tripping is unchanged at
 * the API/persistence level.
 */
import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { BulletNodeView } from './views/BulletNodeView.tsx';
import { MeetingHeaderView } from './views/MeetingHeaderView.tsx';
import { AttendeesBlockView } from './views/AttendeesBlockView.tsx';
import { AttendeeGroupView } from './views/AttendeeGroupView.tsx';
import { ActionItemsBlockView } from './views/ActionItemsBlockView.tsx';
import { ActionItemView } from './views/ActionItemView.tsx';
import { TopicView } from './views/TopicView.tsx';

// ─── Topic / TopicTitle / Bullet ──────────────────────────────────────

/**
 * Topic — the unit of meeting content. Always contains exactly one
 * topicTitle followed by one or more bullets.
 */
export const TopicNode = Node.create({
  name: 'topic',
  group: 'block',
  // `bullet*` (was `bullet+`) so topics can be momentarily empty
  // while the user moves the only bullet out via drag or
  // Alt+↑/↓. The seeder still defaults to one placeholder bullet
  // for new topics, so the UX is unchanged in practice.
  content: 'topicTitle bullet*',
  defining: true,
  // Phase 7: ProseMirror lets the user drag the whole topic to
  // reorder it within the document. Bullets / actionItems get the
  // same flag below. The drag handle is the node's outer DOM —
  // grab anywhere on the topic's left margin (the wider strip
  // outside the content column) and drag it elsewhere.
  draggable: true,

  addAttributes() {
    return {
      key: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-key'),
        renderHTML: (attrs) =>
          attrs.key ? { 'data-key': attrs.key } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'section[data-node="topic"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'section',
      mergeAttributes(HTMLAttributes, {
        'data-node': 'topic',
        class: 'tt-topic',
      }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(TopicView);
  },
});

export const TopicTitleNode = Node.create({
  name: 'topicTitle',
  content: 'text*',
  marks: '_',
  defining: true,

  parseHTML() {
    return [{ tag: 'h2[data-node="topicTitle"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'h2',
      mergeAttributes(HTMLAttributes, {
        'data-node': 'topicTitle',
        class: 'tt-topic-title',
      }),
      0,
    ];
  },
});

export const BulletNode = Node.create({
  name: 'bullet',
  // CRITICAL: bullets are NOT in the 'block' group on purpose.
  // If they were, the doc schema (which accepts `block+` at the
  // top level) would consider a bare bullet a valid doc-level
  // child. dropPoint would then resolve drag positions in the
  // GAP between two topics to a doc-level insertion, leaving the
  // dropped bullet as an orphan outside any topic. The serializer
  // (which only walks topic > bullet) then quietly drops it on
  // save and the user loses content. By scoping bullets to topic
  // (which references them by name in `content: 'topicTitle
  // bullet*'`), the only legal drop target is inside a topic —
  // so dragging across topics works as expected.
  content: 'text*',
  marks: '_',
  defining: true,
  draggable: true,

  addAttributes() {
    return {
      isDirectQuote: {
        default: false,
        parseHTML: (el) => el.getAttribute('data-direct-quote') === 'true',
        renderHTML: (attrs) =>
          attrs.isDirectQuote ? { 'data-direct-quote': 'true' } : {},
      },
      ambiguityFlag: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-ambiguity'),
        renderHTML: (attrs) =>
          attrs.ambiguityFlag
            ? { 'data-ambiguity': attrs.ambiguityFlag }
            : {},
      },
      speaker: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-speaker'),
        renderHTML: (attrs) =>
          attrs.speaker ? { 'data-speaker': attrs.speaker } : {},
      },
      key: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-key'),
        renderHTML: (attrs) =>
          attrs.key ? { 'data-key': attrs.key } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-node="bullet"]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    const classes = ['tt-bullet'];
    if (node.attrs.isDirectQuote) classes.push('tt-bullet--quote');
    if (node.attrs.ambiguityFlag) classes.push('tt-bullet--flag');
    if (node.attrs.speaker) classes.push('tt-bullet--speaker');
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-node': 'bullet',
        class: classes.join(' '),
      }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(BulletNodeView);
  },
});

// ─── Meeting header ───────────────────────────────────────────────────

/**
 * MeetingHeaderNode — non-atom container for the title + meta strip.
 * Phase 6 drops the atom: true flag so children are real editable
 * blocks. Meta fields (date, project, location, format, variant)
 * stay as attrs and render as inline pills via the NodeView.
 */
export const MeetingHeaderNode = Node.create({
  name: 'meetingHeader',
  group: 'block',
  content: 'headerTitle',
  defining: true,

  addAttributes() {
    return {
      project: { default: '' },
      meetingDate: { default: '' },
      notesDate: { default: '' },
      location: { default: '' },
      meetingFormat: { default: 'in_person' },
      templateVariant: { default: 'client_meeting' },
    };
  },

  parseHTML() {
    return [{ tag: 'header[data-node="meetingHeader"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'header',
      mergeAttributes(HTMLAttributes, { 'data-node': 'meetingHeader' }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(MeetingHeaderView);
  },
});

/** HeaderTitleNode — editable h1 (text*) for the meeting title. */
export const HeaderTitleNode = Node.create({
  name: 'headerTitle',
  content: 'text*',
  marks: '_',
  defining: true,

  parseHTML() {
    return [{ tag: 'h1[data-node="headerTitle"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'h1',
      mergeAttributes(HTMLAttributes, {
        'data-node': 'headerTitle',
        class: 'tt-header-title',
      }),
      0,
    ];
  },
});

// ─── Attendees ────────────────────────────────────────────────────────

/**
 * AttendeesBlockNode — non-atom container holding zero or more
 * attendeeGroup children. Phase 6: drops atom: true so the user
 * can place the cursor inside groups, format text, etc.
 */
export const AttendeesBlockNode = Node.create({
  name: 'attendeesBlock',
  group: 'block',
  content: 'attendeeGroup*',
  defining: true,

  parseHTML() {
    return [{ tag: 'section[data-node="attendeesBlock"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'section',
      mergeAttributes(HTMLAttributes, { 'data-node': 'attendeesBlock' }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(AttendeesBlockView);
  },
});

/**
 * AttendeeGroupNode — one row in the attendees block. Holds a firm
 * name + a comma-separated members text.
 */
export const AttendeeGroupNode = Node.create({
  name: 'attendeeGroup',
  // Same scoping rationale as BulletNode / ActionItemNode —
  // attendee groups are NOT in the 'block' group so they can
  // only live inside the attendeesBlock parent.
  content: 'firmText membersText',
  defining: true,
  // Phase 7+: attendee groups join topics / bullets / action
  // items as a reorderable block. The ReorderControls in
  // AttendeeGroupView's gutter handle the mouse path; the
  // Reorder extension's Alt+↑ / Alt+↓ keymap handles the
  // keyboard path.
  draggable: true,

  addAttributes() {
    return {
      isOjb: {
        default: false,
        parseHTML: (el) => el.getAttribute('data-is-ojb') === 'true',
        renderHTML: (attrs) =>
          attrs.isOjb ? { 'data-is-ojb': 'true' } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-node="attendeeGroup"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-node': 'attendeeGroup' }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(AttendeeGroupView);
  },
});

/** FirmTextNode — editable single-line firm name inside an attendee
 *  group. */
export const FirmTextNode = Node.create({
  name: 'firmText',
  content: 'text*',
  marks: '_',
  defining: true,

  parseHTML() {
    return [{ tag: 'span[data-node="firmText"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-node': 'firmText',
        class: 'tt-firm-text',
      }),
      0,
    ];
  },
});

/** MembersTextNode — editable comma-separated members list. */
export const MembersTextNode = Node.create({
  name: 'membersText',
  content: 'text*',
  marks: '_',
  defining: true,

  parseHTML() {
    return [{ tag: 'span[data-node="membersText"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-node': 'membersText',
        class: 'tt-members-text',
      }),
      0,
    ];
  },
});

// ─── Action items ─────────────────────────────────────────────────────

/**
 * ActionItemsBlockNode — non-atom container holding zero or more
 * actionItem children.
 */
export const ActionItemsBlockNode = Node.create({
  name: 'actionItemsBlock',
  group: 'block',
  content: 'actionItem*',
  defining: true,

  parseHTML() {
    return [{ tag: 'section[data-node="actionItemsBlock"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'section',
      mergeAttributes(HTMLAttributes, { 'data-node': 'actionItemsBlock' }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ActionItemsBlockView);
  },
});

/**
 * ActionItemNode — one editable action item. Description is text*;
 * assigneeName + dueDate live as attrs and render via inline pills
 * inside the NodeView's pill row.
 */
export const ActionItemNode = Node.create({
  name: 'actionItem',
  // Same scoping rationale as BulletNode — actionItems are NOT in
  // the 'block' group so they can only live inside the
  // actionItemsBlock parent (which references them by name).
  // Otherwise dropPoint would happily drop an action item at the
  // doc level and the serializer would drop it on save.
  content: 'text*',
  marks: '_',
  defining: true,
  draggable: true,

  addAttributes() {
    return {
      assigneeName: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-assignee') || null,
        renderHTML: (attrs) =>
          attrs.assigneeName ? { 'data-assignee': attrs.assigneeName } : {},
      },
      dueDate: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-due') || null,
        renderHTML: (attrs) =>
          attrs.dueDate ? { 'data-due': attrs.dueDate } : {},
      },
      status: {
        default: 'open',
        parseHTML: (el) => el.getAttribute('data-status') || 'open',
        renderHTML: (attrs) =>
          attrs.status ? { 'data-status': attrs.status } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-node="actionItem"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-node': 'actionItem',
        class: 'tt-action-item',
      }),
      0,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ActionItemView);
  },
});

// Note: date + assignee pills for action items render as React
// buttons inside ActionItemView, backed by attrs on the actionItem
// node. They don't need inline atom nodes of their own — the pill
// UI is presentational and the typed values live on attrs.
